import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Project as ProjectBase } from "@/types";
import { roomTypes, styleTypes } from "@/data/mockData";
import { Download, ArrowLeft, ArrowRight, Loader } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";

// Type guard function to validate project status
const isValidStatus = (status: string): status is ProjectBase['status'] => {
  return ['pending', 'processing', 'completed', 'failed'].includes(status);
};

type Project = ProjectBase & { raw?: any };

const ProjectDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [showOriginal, setShowOriginal] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fallbackImage = "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?q=80&w=800&auto=format&fit=crop";
  const pendingPreviewImage = "https://images.ctfassets.net/1z5g3o44yr4a/6Qw7Z1b6Qw7Z1b6Qw7Z1b6/6e2e2e2e2e2e2e2e2e2e2e2e2e2e2e2e2e2e2e2e/virtual-staging-pred-po.jpg";

  useEffect(() => {
    // Check if user is authenticated
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        toast.error("You must be logged in to view project details");
        navigate("/login");
        return;
      }
    };
    
    checkAuth();
  }, [navigate]);

  // Move fetchProject outside useEffect for reuse
  const fetchProject = async () => {
    if (!id || !user?.id) return;
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq("id", id)
        .eq("user_id", user.id)
        .single();
      if (error) {
        console.error("Error fetching project:", error);
        throw new Error(error.message);
      }
      if (!data) {
        throw new Error("Project not found");
      }
      let projectStatus: Project['status'] = 'pending';
      if (data.status && isValidStatus(data.status)) {
        projectStatus = data.status;
      } else {
        console.warn(`Invalid status value received: ${data.status}, defaulting to 'pending'`);
      }
      const formattedProject = {
        id: data.id,
        userId: data.user_id,
        originalImageUrl: data.original_image_url,
        stagedImageUrl: data.staged_image_url,
        roomType: data.room_type,
        styleType: data.style_type,
        status: projectStatus,
        createdAt: data.created_at,
        // Add raw fields for debug, but do not overwrite status
        raw: data
      };
      setProject(formattedProject);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Could not load project details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProject();
    // Set up real-time subscription for project updates
    const channel = supabase
      .channel('public:projects')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'projects',
          filter: `id=eq.${id}`
        }, 
        (payload) => {
          console.log('Project update received:', payload);
          fetchProject();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [id, user?.id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container py-8 flex items-center justify-center">
          <div className="flex flex-col items-center">
            <Loader className="h-8 w-8 animate-spin text-[#14A48D] mb-2" />
            <p>Loading project...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Project Not Found</h1>
            <p className="mb-6">The project you're looking for doesn't exist or has been removed.</p>
            <Link to="/projects">
              <Button>Back to Projects</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const roomType = roomTypes.find(r => r.id === project.roomType)?.name || "Unknown Room";
  const styleType = styleTypes.find(s => s.id === project.styleType)?.name || "Unknown Style";
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (project) {
    // Debug: log the project object
    console.log('Project detail data:', project);
  }

  // Show waiting state if project is pending/processing and both images are missing
  const isWaitingForWebhook = (project.status === 'pending' || project.status === 'processing') && !project.originalImageUrl && !project.stagedImageUrl;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container py-8">
        <Link to="/projects" className="inline-flex items-center text-[#14A48D] hover:underline mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> Zpět na projekty
        </Link>
        
        <div className="flex flex-col md:flex-row justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold">{roomType} - {styleType}</h1>
            <p className="text-gray-500">Vytvořeno: {formatDate(project.createdAt)}</p>
          </div>
          
          <div className="mt-4 md:mt-0">
            {project.status === 'completed' && project.stagedImageUrl && (
              <>
                <a href={project.stagedImageUrl} download target="_blank" rel="noopener noreferrer">
                  <Button className="flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    Stáhnout zařízený obrázek
                  </Button>
                </a>
                {project.originalImageUrl && (
                  <a
                    href={project.originalImageUrl}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block mt-2 text-xs text-gray-500 hover:underline text-center"
                  >
                    Stáhnout původní fotografii
                  </a>
                )}
              </>
            )}
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="flex justify-between p-4 border-b">
            <h2 className="text-xl font-semibold">Náhled projektu</h2>
            <StatusBadge status={project.status} />
          </div>
          {isWaitingForWebhook ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="h-12 w-12 text-[#14A48D] animate-spin mb-3" />
              <h3 className="text-xl font-medium mb-2">Waiting for processing...</h3>
              <p className="text-gray-500 max-w-md text-center">
                Your project is being processed. This page will update automatically when the images are ready.
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-col md:flex-row gap-4 p-4">
                {/* Original Image */}
                <div className="flex-1 flex flex-col items-center">
                  <span className="mb-2 font-medium">Původní</span>
                  <div className="aspect-[4/3] w-full bg-gray-100 rounded overflow-hidden flex items-center justify-center">
                    <img
                      src={project.originalImageUrl || fallbackImage}
                      alt="Původní místnost"
                      className="w-full h-full object-contain"
                    />
                    {!project.originalImageUrl && project.status !== 'pending' && (
                      <span className="absolute text-gray-500">No original image</span>
                    )}
                  </div>
                </div>
                {/* Staged Image */}
                <div className="flex-1 flex flex-col items-center">
                  <span className="mb-2 font-medium">Zařízený</span>
                  <div className="aspect-[4/3] w-full bg-gray-100 rounded overflow-hidden flex items-center justify-center">
                    <img
                      src={project.stagedImageUrl || fallbackImage}
                      alt="Zařízená místnost"
                      className="w-full h-full object-contain"
                    />
                    {!project.stagedImageUrl && (
                      <span className="absolute text-gray-500">No staged image</span>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
          
          <div className="p-4 border-t">
            <h3 className="font-medium mb-2">Detaily projektu</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Typ místnosti</p>
                <p>{roomType}</p>
              </div>
              <div>
                <p className="text-gray-500">Designový styl</p>
                <p>{styleType}</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const StatusBadge = ({ status }: { status: Project['status'] }) => {
  switch (status) {
    case 'pending':
      return <Badge variant="outline" className="text-yellow-500 border-yellow-500 bg-yellow-50">Pending</Badge>;
    case 'processing':
      return <Badge variant="outline" className="text-blue-500 border-blue-500 bg-blue-50">Processing</Badge>;
    case 'completed':
      return <Badge variant="outline" className="text-green-500 border-green-500 bg-green-50">Dokončeno</Badge>;
    case 'failed':
      return <Badge variant="outline" className="text-red-500 border-red-500 bg-red-50">Failed</Badge>;
    default:
      return null;
  }
};

export default ProjectDetail;
